import React from 'react';
import { Heart, MessageCircle, Bookmark, Share2, Music } from 'lucide-react';
import { MediaDisplay } from './MediaDisplay';
import { PreviewProps, getFollowersCount, formatFollowersCount } from './types';

// Removed unused isVideo helper

export const TikTokPreview: React.FC<PreviewProps> = ({ content, brand, mediaUrl }) => {
  // Get the TikTok account from brand's social accounts if available
  const getTikTokAccount = () => {
    if (brand?.social_accounts && Array.isArray(brand.social_accounts)) {
      return brand.social_accounts.find(
        account => account.platform.toLowerCase() === 'tiktok'
      );
    }
    return null;
  };
  
  const tiktokAccount = getTikTokAccount();
  
  // Récupérer le nombre de followers TikTok pour des valeurs par défaut plus réalistes
  const tiktokFollowers = getFollowersCount(brand, 'tiktok');
  
  // Calculer des valeurs par défaut basées sur les followers (engagement TikTok typique ~5-10%)
  const defaultLikes = tiktokFollowers ? Math.floor(tiktokFollowers * 0.08) : 245700;
  const defaultComments = tiktokFollowers ? Math.floor(tiktokFollowers * 0.02) : 1893;
  const defaultShares = tiktokFollowers ? Math.floor(tiktokFollowers * 0.01) : 5621;
  const defaultSaves = tiktokFollowers ? Math.floor(tiktokFollowers * 0.03) : 84200;
  
  // Get the TikTok username
  const getTikTokUsername = (): string => {
    if (tiktokAccount?.username) {
      return tiktokAccount.username;
    }
    
    // Fallback to the old behavior if no TikTok account found
    return brand?.name || 'companyname';
  };
  
  // Get the profile picture
  const getProfilePicture = (): string => {
    if (tiktokAccount?.picture_url) {
      return tiktokAccount.picture_url;
    }
    
    // Fallback to the brand logo
    return brand?.logo || '';
  };

  // TikTok is primarily a short-form video platform, so we'll use the same format for both text and reel
  return (
    <div className="bg-black text-white rounded-xl shadow-md border border-gray-800 overflow-hidden max-w-[320px] mx-auto">
      <div className="relative w-full aspect-[9/16] bg-gray-900 flex flex-col">
        {/* Main content */}
        <div className="flex-1 relative">
          <MediaDisplay
            mediaUrl={mediaUrl}
            altText="TikTok video"
            type="video"
            fullSize={false}
            className="h-full w-full object-cover"
            controls={false}
            autoPlayOnMount
            loop
            muted
            aspectRatio="9/16"
            key={mediaUrl}
          />
          
          {/* Right side actions */}
          <div className="absolute right-3 bottom-20 flex flex-col items-center space-y-5">
            <div className="flex flex-col items-center">
              <div
                className="h-10 w-10 bg-gray-100 overflow-hidden flex-shrink-0 mb-1"
                style={{ borderRadius: '50%' }}
              >
                {getProfilePicture() ? (
                  <img
                    src={getProfilePicture()}
                    alt=""
                    className="w-full h-full object-cover"
                    style={{ borderRadius: '50%' }}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200" />
                )}
              </div>
              <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center -mt-3 text-xs font-bold">+</div>
            </div>
            <button className="flex flex-col items-center">
              <Heart className="w-8 h-8" />
              <span className="text-xs mt-1">{formatFollowersCount(defaultLikes)}</span>
            </button>
            <button className="flex flex-col items-center">
              <MessageCircle className="w-8 h-8" />
              <span className="text-xs mt-1">{formatFollowersCount(defaultComments)}</span>
            </button>
            <button className="flex flex-col items-center">
              <Bookmark className="w-8 h-8" />
              <span className="text-xs mt-1">{formatFollowersCount(defaultSaves)}</span>
            </button>
            <button className="flex flex-col items-center">
              <Share2 className="w-8 h-8" />
              <span className="text-xs mt-1">{formatFollowersCount(defaultShares)}</span>
            </button>
            {/* Bottom profile circle */}
            <div
              className="h-10 w-10 flex-none bg-white/20 backdrop-blur-sm overflow-hidden"
              style={{ borderRadius: '50%' }}
            >
              {getProfilePicture() ? (
                <img
                  src={getProfilePicture()}
                  alt=""
                  className="w-full h-full object-cover"
                  style={{ borderRadius: '50%' }}
                />
              ) : (
                <div className="w-full h-full bg-gray-200" />
              )}
            </div>
          </div>

          {/* Bottom caption */}
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-semibold">@{getTikTokUsername()}</span>
            </div>
            <p className="mb-3 whitespace-pre-line" style={{ fontSize: '0.8rem' }}>{content}</p>
            <div className="flex items-center">
              <Music className="w-3 h-3 mr-2" />
              <div className="text-xs marquee-container overflow-hidden">
                <div className="animate-marquee whitespace-nowrap">
                  {brand?.name || 'companyname'} - Original Sound
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};